import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import LogRocket from 'logrocket';

import { useOrderDetailsSessionContext } from './order-details-session-context';
import { ORDER_DETAILS_ACTION, DELETE_SHIPMENT_ACTION } from '../../../../constants/analytics-events';
import { Segment } from '../../../../services/analytics-services/segment';
import Global from '../../../../store/global-selectors';
import { selectOrderDetailsSalesOrderId } from '../../../../store/orders/order-details/orders.details.selectors';
import { useFulfillmentPlanHasAdditionalItemsAndCount } from '../../../../fulfillment-plans/delete-modal/hooks';

export const ORDER_DETAILS_ACTIONS = {
  PRINT_PACKING_SLIPS: 'PRINT_PACKING_SLIPS',
  PRINT_LABELS: 'PRINT_LABELS',
  PRINT_CUSTOMS_FORMS: 'PRINT_CUSTOMS_FORMS',
  PRINT_PICK_LISTS: 'PRINT_PICK_LISTS',
  PRINT_SHIPMENT_MANIFEST: 'PRINT_SHIPMENT_MANIFEST',
  PRINT_ORDER_SUMMARY: 'PRINT_ORDER_SUMMARY',
  EDIT_ORDER_SUMMARY: 'EDIT_ORDER_SUMMARY',
  EDIT_ORDER_RECIPIENT: 'EDIT_ORDER_RECIPIENT',
  EDIT_ORDER_TAX_IDENTIFIER: 'EDIT_ORDER_TAX_IDENTIFIER',
  SHIPMENTS_PRINT_LABELS: 'SHIPMENTS_PRINT_LABELS',
  SHIPMENTS_PRINT_PACKING_SLIPS: 'SHIPMENTS_PRINT_PACKING_SLIPS',
  SHIPMENTS_PRINT_ORDER_SUMMARY: 'SHIPMENTS_PRINT_ORDER_SUMMARY',
  SHIPMENTS_PRINT_PICK_LIST: 'SHIPMENTS_PRINT_PICK_LIST',
  SHIPMENTS_PRINT_SHIPMENT_MANIFEST: 'SHIPMENTS_PRINT_SHIPMENT_MANIFEST',
  SHIPMENTS_PRINT_OTHER_FORMS: 'SHIPMENTS_PRINT_OTHER_FORMS',
  SHIPMENTS_VOID: 'SHIPMENTS_VOID',
  ADD_SALES_ORDER_TO_BATCH: 'ADD_SALES_ORDER_TO_BATCH',
  ADD_FULFILLMENT_PLAN_TO_BATCH: 'ADD_FULFILLMENT_PLAN_TO_BATCH',
  CREATE_BATCH_ADD_FULFILLMENT_PLAN: 'CREATE_BATCH_ADD_FULFILLMENT_PLAN',
  CREATE_RETURN: 'CREATE_RETURN',
  CREATE_ANOTHER_SHIPMENT: 'CREATE_ANOTHER_SHIPMENT',
  ALLOCATE_INVENTORY: 'ALLOCATE_INVENTORY',
  ADD_EDIT_SHIPMENT_ITEMS: 'ADD_EDIT_SHIPMENT_ITEMS',
  DELETE_SHIPMENT: 'DELETE_SHIPMENT',
  RESHIP_SHIPMENT: 'RESHIP_SHIPMENT',
};

export const DELETE_SHIPMENT_ACTIONS = {
  OPEN: 'OPEN',
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
  CLOSE: 'CLOSE',
};

const logRocketDeleteEventName = 'DELETE_SHIPMENT';

export const useTrackOrderDetailsAction = (action) => {
  const { version, sessionId } = useOrderDetailsSessionContext();
  const salesOrderId = useSelector(selectOrderDetailsSalesOrderId);
  const fulfillmentPlanIds = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanIdsBySalesOrderIds(state, {
      salesOrderIds: [salesOrderId],
    }),
  );
  const fulfillments = useSelector((state) =>
    Global.selectEntitiesFulfillmentsByFulfillmentPlanIds(state, {
      fulfillmentPlanIds,
    }),
  );

  return useCallback(() => {
    const trackEvent = () => {
      const unvoidedFulfillments = fulfillments.filter((fulfillment) => !fulfillment.isVoided);
      const fulfillmentIds = unvoidedFulfillments.map((fulfillment) => fulfillment.fulfillmentId);

      const fulfillmentCountsByType = fulfillments.reduce((groups, fulfillment) => {
        const groupKey = `${fulfillment.fulfillmentType}FulfillmentCount`;
        return {
          ...groups,
          [groupKey]: (groups[groupKey] ?? 0) + 1,
        };
      }, {});

      Segment.trackEvent(ORDER_DETAILS_ACTION, {
        version,
        sessionId,
        action,
        salesOrderId,
        fulfillmentPlanIds,
        fulfillmentPlanCount: fulfillmentIds.length,
        fulfillmentIds,
        fulfillmentCount: fulfillmentIds.length,
        ...fulfillmentCountsByType,
      });
    };
    setTimeout(trackEvent, 0);
  }, [sessionId, version, action, salesOrderId, fulfillmentPlanIds, fulfillments]);
};

export const useTrackDeleteShipmentAction = (action, fulfillmentPlanId) => {
  const { sessionId } = useOrderDetailsSessionContext();
  const { hasAdditionaItems } = useFulfillmentPlanHasAdditionalItemsAndCount(fulfillmentPlanId);

  return useCallback(
    (error) => {
      const trackEvent = () => {
        if (action === DELETE_SHIPMENT_ACTIONS.OPEN) {
          const environment = process.env.NODE_ENV || 'development'; // "development" or "production"
          LogRocket.track(logRocketDeleteEventName, {
            env: environment,
          });
        }
        Segment.trackEvent(DELETE_SHIPMENT_ACTION, {
          parentAction: ORDER_DETAILS_ACTION,
          action,
          error,
          sessionId,
          fulfillmentPlanId,
          hasAdditionaItems,
        });
      };
      setTimeout(trackEvent, 0);
    },
    [hasAdditionaItems, action, sessionId, fulfillmentPlanId],
  );
};
